import type {
	ApproveBudgetData,
	ApproveBudgetResponse,
	AssignActingManagerResponse,
	BudgetDecisionData,
	BudgetReviewsResponse,
	CompensationCSVRequestModel,
	CompensationCSVResponse,
	CompensationCycleListResponse,
	CompensationCycleResponse,
	CompensationDecisionsWidgetResponse,
	CompensationSettingsResponse,
	CompleteCompensationCycleResponse,
	CreateCompensationCycleRequest,
	CreateCompensationCycleResponse,
	DeleteCompensationCycleResponse,
	DismissSalaryVisConflictBannerResponse,
	EditBudgetDecisionResponse,
	GetCompanySalaryCustomFieldData,
	GetCompanySalaryCustomFieldId,
	GetCycleUsersWithoutCompensationDataResponse,
	GetSelectionManagersResponse,
	GetUserHasDismissedSalaryBannerData,
	GetUsersWithoutCompensationDataResponse,
	MyDecisionsResponse,
	SelectionSalarySummaryResponse,
	SubmitDecisionsRequest,
	SubmitDecisionsResponse,
	UpdateCompensationCycleOwnersResponse,
	UpdateCompensationCycleRequest,
	UpdateMyDecisionsNotesRequest,
	UpdateMyDecisionsNotesResponse,
} from "@performyard/service-contracts";
import type { ConvertObjectIdsAndDatesToString, ConvertObjectIdsToString } from "@performyard/utils-isomorphic";

import internalRequest from "./internal-base-request";

export const getCompensationSettings = async () => {
	const [response] =
		await internalRequest<ConvertObjectIdsAndDatesToString<CompensationSettingsResponse>>(
			"/v1/compensation/settings"
		);
	return response;
};

export const updateCompensationCycleOwners = async (cycleOwnerIds: string[]) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<UpdateCompensationCycleOwnersResponse>>(
		"/v1/compensation/cycle-owners",
		{
			method: "PUT",
			body: JSON.stringify({ cycleOwnerIds }),
		}
	);
	return response;
};

export const addCompensationCycleOwner = async (userId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<UpdateCompensationCycleOwnersResponse>>(
		`/v1/compensation/cycle-owners/${userId}`,
		{ method: "POST" }
	);
	return response;
};

export const removeCompensationCycleOwner = async (userId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<UpdateCompensationCycleOwnersResponse>>(
		`/v1/compensation/cycle-owners/${userId}`,
		{ method: "DELETE" }
	);
	return response;
};

export const createCompensationCycle = async (data: CreateCompensationCycleRequest) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CreateCompensationCycleResponse>>(
		"/v1/compensation/cycles",
		{
			method: "POST",
			body: JSON.stringify(data),
		}
	);
	return response;
};

export const listCompensationCycles = async (params: {
	skip?: number;
	limit?: number;
	sort?: string;
	search?: string;
	status?: string;
}) => {
	const queryParams = new URLSearchParams();
	if (params.skip !== undefined) queryParams.append("skip", params.skip.toString());
	if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
	if (params.sort) queryParams.append("sort", params.sort);
	if (params.search) queryParams.append("search", params.search);
	if (params.status) queryParams.append("status", params.status);

	const queryString = queryParams.toString();
	const url = `/v1/compensation/cycles${queryString ? `?${queryString}` : ""}`;

	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CompensationCycleListResponse>>(url);
	return response;
};

export const listCompensationCyclesWithMyDecisions = async (params: {
	skip?: number;
	limit?: number;
	sort?: string;
	search?: string;
	status?: string;
}) => {
	const queryParams = new URLSearchParams();
	if (params.skip !== undefined) queryParams.append("skip", params.skip.toString());
	if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
	if (params.sort) queryParams.append("sort", params.sort);
	if (params.search) queryParams.append("search", params.search);
	if (params.status) queryParams.append("status", params.status);

	const queryString = queryParams.toString();
	const url = `/v1/compensation/cycles/my-cycles-with-decisions${queryString ? `?${queryString}` : ""}`;

	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CompensationCycleListResponse>>(url);
	return response;
};

export const getCompensationDecisionsWidget = async () => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CompensationDecisionsWidgetResponse>>(
		"/v1/compensation/cycles/my-decisions-widget"
	);
	return response;
};

export const deleteCompensationCycle = async (cycleId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<DeleteCompensationCycleResponse>>(
		`/v1/compensation/cycles/${cycleId}`,
		{
			method: "DELETE",
		}
	);
	return response;
};

export const getSelectionSalarySummary = async (userSelectionId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<SelectionSalarySummaryResponse>>(
		`/v1/compensation/selection/${userSelectionId}/salary-summary`
	);
	return response;
};

export const getSelectionManagers = async (
	userSelectionId: string,
	params?: {
		skip?: number;
		limit?: number;
		sortBy?: "name" | "directReportCount" | "totalCompensation";
		sortOrder?: "asc" | "desc";
		search?: string;
	}
) => {
	const queryParams = new URLSearchParams();
	if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
	if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
	if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
	if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);
	if (params?.search) queryParams.append("search", params.search);

	const queryString = queryParams.toString();
	const url = `/v1/compensation/selection/${userSelectionId}/managers${queryString ? `?${queryString}` : ""}`;

	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<GetSelectionManagersResponse>>(url);
	return response;
};

export const getCompensationCycleDetails = async (cycleId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CompensationCycleResponse>>(
		`/v1/compensation/cycles/${cycleId}`
	);
	return response;
};

export const getCompensationMyDecisionDetails = async (userId: string, cycleId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsToString<MyDecisionsResponse>>(
		`/v1/compensation/cycles/${cycleId}/my-decisions`
	);
	return response;
};
export const fetchUsersWithoutCompensationData = async () => {
	const [response] = await internalRequest<GetUsersWithoutCompensationDataResponse>(
		`/v1/compensation/users-without-compensation-data`
	);
	return response;
};

export const getCompanySalaryCustomFieldData = async () => {
	const [response] = await internalRequest<GetCompanySalaryCustomFieldData>(
		`/v1/compensation/company-salary-custom-field-data`
	);
	return response;
};

export const getUserSalaryBannerDismissalData = async () => {
	const [response] = await internalRequest<GetUserHasDismissedSalaryBannerData>(
		`/v1/compensation/user-salary-vis-banner-data`
	);
	return response;
};

export const dismissSalaryFieldVisibilityConflictBanner = async () => {
	const [response] = await internalRequest<DismissSalaryVisConflictBannerResponse>(
		`/v1/compensation/dismiss-salary-field-vis-conflict-banner`,
		{
			method: "POST",
		}
	);
	return response;
};

export const updateMyDecisionsNotes = async (data: UpdateMyDecisionsNotesRequest) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<UpdateMyDecisionsNotesResponse>>(
		`/v1/compensation/my-decisions-notes`,
		{
			method: "PATCH",
			body: JSON.stringify(data),
		}
	);
	return response;
};

export const submitDecisions = async (data: SubmitDecisionsRequest) => {
	const [response] = await internalRequest<SubmitDecisionsResponse>(`/v1/compensation/submit-decisions`, {
		method: "POST",
		body: JSON.stringify(data),
	});
	return response;
};

export const fetchCompensationCycleBudgetReviewData = async (cycleId: string) => {
	const [response] = await internalRequest<BudgetReviewsResponse>(
		`/v1/compensation/cycles/${cycleId}/budget-reviews`
	);
	return response;
};

export const updateActingManager = async (cycleId: string, currentManagerId: string, actingManagerId: string) => {
	const data = { cycleId, currentManagerId, actingManagerId };
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<AssignActingManagerResponse>>(
		`/v1/compensation/assign-acting-manager`,
		{
			method: "PATCH",
			body: JSON.stringify(data),
		}
	);
	return response;
};

export const approveBudget = async (cycleId: string, approverId: string, budgetData: ApproveBudgetData) => {
	const data = { cycleId, approverId, budgetData };
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<ApproveBudgetResponse>>(
		`/v1/compensation/approve-budget`,
		{
			method: "POST",
			body: JSON.stringify(data),
		}
	);
	return response;
};

export const editBudgetDecision = async (cycleId: string, budgetDecisionData: BudgetDecisionData) => {
	const data = { cycleId, budgetDecisionData };
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<EditBudgetDecisionResponse>>(
		`/v1/compensation/edit-budget-decision`,
		{
			method: "POST",
			body: JSON.stringify(data),
		}
	);
	return response;
};

export const bulkApproveBudget = async (cycleId: string, approverId: string, budgetData: ApproveBudgetData[]) => {
	const data = { cycleId, approverId, budgetData };
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<ApproveBudgetResponse>>(
		`/v1/compensation/bulk-approve-budget`,
		{
			method: "POST",
			body: JSON.stringify(data),
		}
	);
	return response;
};

export const completeCompensationCycle = async (cycleId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CompleteCompensationCycleResponse>>(
		`/v1/compensation/cycles/${cycleId}/complete`,
		{
			method: "POST",
		}
	);
	return response;
};

export const updateCompensationCycleDetails = async (cycleId: string, data: UpdateCompensationCycleRequest) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CompleteCompensationCycleResponse>>(
		`/v1/compensation/cycles/${cycleId}/edit-details`,
		{
			method: "PATCH",
			body: JSON.stringify(data),
		}
	);
	return response;
};

export const reopenCompensationCycle = async (cycleId: string) => {
	const [response] = await internalRequest<ConvertObjectIdsAndDatesToString<CompleteCompensationCycleResponse>>(
		`/v1/compensation/cycles/${cycleId}/reopen`,
		{
			method: "POST",
		}
	);
	return response;
};

export const downloadCompensationCycleCSV = async (
	cycleId: string,
	data: CompensationCSVRequestModel
): Promise<CompensationCSVResponse> => {
	const [responseData] = await internalRequest<ConvertObjectIdsToString<CompensationCSVResponse>>(
		`/v1/compensation/cycles/${cycleId}/csv`,
		{
			method: "POST",
			body: JSON.stringify(data),
		}
	);
	return responseData;
};

export const getCompanySalaryCustomFieldId = async () => {
	const [response] = await internalRequest<GetCompanySalaryCustomFieldId>(
		`/v1/compensation/company-salary-custom-field-id`
	);
	return response;
};

export const fetchCycleHasUsersWithoutCompensationData = async (cycleId: string) => {
	const [response] = await internalRequest<GetCycleUsersWithoutCompensationDataResponse>(
		`/v1/compensation/users-without-compensation-data/cycle/${cycleId}`
	);
	return response;
};
